import React, { useMemo, useState } from 'react';
import moment from 'moment';

import DatePickerApp from '../../../../components/UI/DatePicker';
import DateRangePicker from '../../../../components/UI/DateRangePicker';
import {
  getViolationWeekRange,
  VIOLATION_PERIOD_OPTIONS,
  VIOLATION_PERIOD_TYPES
} from '../../../../utils/attendanceViolationHelpers';

const todayStr = () => moment().format('YYYY-MM-DD');

const ViolationPeriodFilters = ({ state, setState }) => {
  const periodType = state.periodType || VIOLATION_PERIOD_TYPES.MONTH;
  const [rangeOpen, setRangeOpen] = useState({ startOpen: false, endOpen: false });

  const resetPage = (patch) => {
    setState((prev) => ({ ...prev, ...patch, currentPage: 1 }));
  };

  const anchorDate = useMemo(() => moment(state.date || todayStr(), 'YYYY-MM-DD'), [state.date]);

  const monthDate = useMemo(
    () => moment({ year: state.year, month: (state.month || 1) - 1 }),
    [state.month, state.year]
  );

  const startMoment = state.startDate ? moment(state.startDate, 'YYYY-MM-DD') : null;
  const endMoment = state.endDate ? moment(state.endDate, 'YYYY-MM-DD') : null;

  const handlePeriodTypeChange = (nextType) => {
    const today = moment();
    setState((prev) => {
      const next = { ...prev, periodType: nextType, currentPage: 1 };

      if (nextType === VIOLATION_PERIOD_TYPES.MONTH) {
        const anchor = moment(prev.date || undefined);
        next.month = anchor.month() + 1;
        next.year = anchor.year();
      }

      if (nextType === VIOLATION_PERIOD_TYPES.DAY || nextType === VIOLATION_PERIOD_TYPES.WEEK) {
        next.date = prev.date || todayStr();
      }

      if (nextType === VIOLATION_PERIOD_TYPES.CUSTOM) {
        next.startDate = prev.startDate || today.clone().startOf('month').format('YYYY-MM-DD');
        next.endDate = prev.endDate || todayStr();
      }

      return next;
    });
    setRangeOpen({ startOpen: false, endOpen: false });
  };

  const handleDayWeekChange = (picked) => {
    if (!picked) return;
    resetPage({ date: picked.format('YYYY-MM-DD') });
  };

  const handleMonthChange = (picked) => {
    if (!picked) return;
    resetPage({
      month: picked.month() + 1,
      year: picked.year(),
      date: picked.format('YYYY-MM-DD')
    });
  };

  const onRangeStartChange = (value) => {
    const start = value?.[0] ? value[0].format('YYYY-MM-DD') : state.startDate;
    const end = value?.[1] ? value[1].format('YYYY-MM-DD') : state.endDate;
    resetPage({ startDate: start, endDate: end });
    setRangeOpen({ startOpen: false, endOpen: Boolean(value?.[0] && !value?.[1]) });
  };

  const onRangeEndChange = (value) => {
    const start = value?.[0] ? value[0].format('YYYY-MM-DD') : state.startDate;
    const end = value?.[1] ? value[1].format('YYYY-MM-DD') : state.endDate;
    resetPage({ startDate: start, endDate: end });
    setRangeOpen({ startOpen: false, endOpen: false });
  };

  const disabledStartDate = (current) => {
    if (!current) return false;
    if (current.isAfter(moment(), 'day')) return true;
    if (!endMoment) return false;
    return current.isAfter(endMoment, 'day');
  };

  const weekRange = useMemo(() => getViolationWeekRange(state.date), [state.date]);

  const canShiftDate =
    periodType === VIOLATION_PERIOD_TYPES.DAY || periodType === VIOLATION_PERIOD_TYPES.WEEK;

  const isCurrentPeriod = useMemo(() => {
    const today = moment();

    if (periodType === VIOLATION_PERIOD_TYPES.DAY) {
      return anchorDate.isSame(today, 'day');
    }

    if (periodType === VIOLATION_PERIOD_TYPES.WEEK) {
      return today.isBetween(weekRange.start, weekRange.end, 'day', '[]');
    }

    if (periodType === VIOLATION_PERIOD_TYPES.MONTH) {
      return monthDate.month() === today.month() && monthDate.year() === today.year();
    }

    return true;
  }, [anchorDate, monthDate, periodType, weekRange.end, weekRange.start]);

  const shiftDate = (direction) => {
    if (direction > 0 && isCurrentPeriod) return;

    const unit = periodType === VIOLATION_PERIOD_TYPES.WEEK ? 'week' : 'day';
    const next = anchorDate.clone().add(direction, unit);

    if (direction > 0) {
      const today = moment();
      if (periodType === VIOLATION_PERIOD_TYPES.WEEK) {
        if (next.clone().startOf('week').isAfter(today.clone().startOf('week'))) return;
      } else if (next.isAfter(today, 'day')) {
        return;
      }
    }

    resetPage({ date: next.format('YYYY-MM-DD') });
  };

  const jumpToToday = () => {
    const today = moment();

    if (periodType === VIOLATION_PERIOD_TYPES.DAY || periodType === VIOLATION_PERIOD_TYPES.WEEK) {
      resetPage({ date: today.format('YYYY-MM-DD') });
      return;
    }

    if (periodType === VIOLATION_PERIOD_TYPES.MONTH) {
      resetPage({
        month: today.month() + 1,
        year: today.year(),
        date: today.format('YYYY-MM-DD')
      });
    }
  };

  const renderWeekDisplay = () => {
    const sameYear = weekRange.start.year() === weekRange.end.year();
    const startLabel = sameYear
      ? weekRange.start.format('DD MMM')
      : weekRange.start.format('DD MMM YYYY');

    return (
      <div className="att-violation-date-display" aria-hidden>
        <span className="att-violation-date-display__part">{startLabel}</span>
        <span className="att-violation-date-display__sep">–</span>
        <span className="att-violation-date-display__part att-violation-date-display__part--end">
          {weekRange.end.format('DD MMM YYYY')}
        </span>
      </div>
    );
  };

  const renderDateControl = () => {
    if (periodType === VIOLATION_PERIOD_TYPES.DAY) {
      return (
        <DatePickerApp type="Day" initialDate={anchorDate} handleChangeDate={handleDayWeekChange} />
      );
    }

    if (periodType === VIOLATION_PERIOD_TYPES.WEEK) {
      return (
        <div className="att-violation-date-control__picker-wrap att-violation-date-control__picker-wrap--week">
          {renderWeekDisplay()}
          <DatePickerApp
            type="Week"
            initialDate={anchorDate}
            handleChangeDate={handleDayWeekChange}
          />
        </div>
      );
    }

    if (periodType === VIOLATION_PERIOD_TYPES.MONTH) {
      return (
        <DatePickerApp type="Month" initialDate={monthDate} handleChangeDate={handleMonthChange} />
      );
    }

    return (
      <DateRangePicker
        startValue={startMoment}
        endValue={endMoment}
        onStartChange={onRangeStartChange}
        onEndChange={onRangeEndChange}
        disabledStartDate={disabledStartDate}
        startOpen={rangeOpen.startOpen}
        endOpen={rangeOpen.endOpen}
        onStartOpenChange={(startOpen) => setRangeOpen((prev) => ({ ...prev, startOpen }))}
        onEndOpenChange={(endOpen) => setRangeOpen((prev) => ({ ...prev, endOpen }))}
      />
    );
  };

  return (
    <div className="att-violation-filter-bar__primary">
      <div className="att-violation-period-segment" role="tablist" aria-label="Violation period">
        {VIOLATION_PERIOD_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={periodType === option.value}
            className={`att-violation-period-segment__tab${
              periodType === option.value ? ' att-violation-period-segment__tab--active' : ''
            }`}
            onClick={() => handlePeriodTypeChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div
        className={`att-violation-date-control att-violation-date-control--${periodType.toLowerCase()}${
          canShiftDate ? ' att-violation-date-control--navigable' : ''
        }`}
      >
        {canShiftDate ? (
          <button
            type="button"
            className="att-violation-date-control__nav att-violation-date-control__nav--prev"
            aria-label="Previous period"
            onClick={() => shiftDate(-1)}
          >
            <i className="bi bi-chevron-left" aria-hidden />
          </button>
        ) : null}

        <div className="att-violation-date-control__body">
          <i className="bi bi-calendar3 att-violation-date-control__icon" aria-hidden />
          <div className="att-violation-date-control__picker">{renderDateControl()}</div>
          {!isCurrentPeriod && periodType !== VIOLATION_PERIOD_TYPES.CUSTOM ? (
            <button
              type="button"
              className="att-violation-date-control__today"
              onClick={jumpToToday}
            >
              Today
            </button>
          ) : null}
        </div>

        {canShiftDate ? (
          <button
            type="button"
            className="att-violation-date-control__nav att-violation-date-control__nav--next"
            aria-label="Next period"
            disabled={isCurrentPeriod}
            onClick={() => shiftDate(1)}
          >
            <i className="bi bi-chevron-right" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ViolationPeriodFilters;
